import { useState } from "react";
import { tempoRelativo } from "../formatos";
import { IconeSino } from "../icones";
import { LinkInterno } from "../navegacao";
import { Carregando } from "./Basicos";
import { Botao } from "./Botao";
import { Flutuante } from "./Flutuante";

export type Notificacao = { id: string; titulo: string; mensagem: string; criada_em: string; lida: boolean; link?: string | null };

/** Um aviso na lista: ponto de nao lido, titulo, mensagem e quando. Tocar abre (o app marca como lida e leva ao link). */
function ItemDoAviso({ n, aoAbrir, aoMarcarLida }: { n: Notificacao; aoAbrir: () => void; aoMarcarLida?: () => void }) {
  return (
    <li className="mtc-sino__item" data-lida={n.lida}>
      <button type="button" onClick={aoAbrir}>
        <span className="mtc-sino__ponto" aria-hidden />
        <span className="mtc-sino__textos">
          <span className="mtc-sino__titulo">{n.titulo}</span>
          <span className="mtc-sino__mensagem">{n.mensagem}</span>
          <span className="mtc-sino__quando">{tempoRelativo(n.criada_em)}{n.lida ? "" : ". Não lida"}</span>
        </span>
      </button>
      {!n.lida && aoMarcarLida && (
        <div className="mtc-sino__acoes">
          <Botao variante="fantasma" tamanho="pequeno" onClick={aoMarcarLida}>Marcar como lida</Botao>
        </div>
      )}
    </li>
  );
}

/**
 * A lista de avisos fora do sino (pagina de notificacoes, ultimas no Inicio). `aoMarcarLida` mostra o "Marcar como
 * lida" nos nao lidos. So apresentacao: quem busca, marca e navega e o app.
 */
export function ListaDeNotificacoes({ itens, aoAbrir, aoMarcarLida, rotulo = "Notificações" }: { itens: Notificacao[]; aoAbrir: (n: Notificacao) => void; aoMarcarLida?: (n: Notificacao) => void; rotulo?: string }) {
  return (
    <ul className="mtc-sino__lista mtc-notificacoes" aria-label={rotulo}>
      {itens.map((n) => <ItemDoAviso key={n.id} n={n} aoAbrir={() => aoAbrir(n)} aoMarcarLida={aoMarcarLida ? () => aoMarcarLida(n) : undefined} />)}
    </ul>
  );
}

/**
 * Sino de notificacoes: contagem de nao lidas, lista, abrir (marca como lida e leva ao link) e marcar todas.
 * So apresentacao; quem busca, marca e navega e o app. Abre pelo `Flutuante`: no celular e a folha inferior do
 * `Dialogo` (fundo escurecido e Fechar); da largura do trilho pra cima, um painel ancorado no botao. Tocar fora so fecha.
 * `lado` diz pra que lado o painel cresce a partir do botao ("esquerda": o botao esta na esquerda da tela e o painel
 * cresce pra direita, como na gaveta da area e na lateral do painel). `verTodasHref` poe o "Ver todas" no pe.
 */
export function Sino({ naoLidas, itens, carregando, aoAbrirLista, aoAbrirItem, aoMarcarTodas, lado = "direita", verTodasHref }: { naoLidas: number; itens: Notificacao[]; carregando?: boolean; aoAbrirLista?: () => void; aoAbrirItem: (n: Notificacao) => void; aoMarcarTodas?: () => void; lado?: "direita" | "esquerda"; verTodasHref?: string }) {
  const [aberto, setAberto] = useState(false);
  const alternar = () => {
    const abrir = !aberto;
    setAberto(abrir);
    if (abrir) aoAbrirLista?.();
  };
  return (
    <div className="mtc-sino">
      <button type="button" className="mtc-sino__botao" aria-label={naoLidas ? `Notificações, ${naoLidas} não lidas` : "Notificações"} aria-expanded={aberto} aria-haspopup="dialog" onClick={alternar}>
        <IconeSino tamanho={20} />
        {naoLidas > 0 && <span className="mtc-sino__contagem" aria-hidden>{naoLidas > 99 ? "99+" : naoLidas}</span>}
      </button>
      <Flutuante
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo="Notificações"
        posicao={lado === "esquerda" ? "abaixo-esquerda" : "abaixo-direita"}
        classe="mtc-sino__flutuante"
        acao={naoLidas > 0 && aoMarcarTodas ? <Botao variante="fantasma" tamanho="pequeno" onClick={aoMarcarTodas}>Marcar todas como lidas</Botao> : undefined}
      >
        {carregando && !itens.length ? (
          <Carregando compacto texto="Buscando" />
        ) : !itens.length ? (
          <p className="mtc-sino__vazio">Nenhuma notificação por enquanto.</p>
        ) : (
          <ul className="mtc-sino__lista">
            {itens.map((n) => <ItemDoAviso key={n.id} n={n} aoAbrir={() => { setAberto(false); aoAbrirItem(n); }} />)}
          </ul>
        )}
        {verTodasHref && (
          <div className="mtc-sino__rodape">
            <LinkInterno href={verTodasHref} className="mtc-botao mtc-botao--fantasma mtc-botao--pequeno" onClick={() => setAberto(false)}>Ver todas as notificações</LinkInterno>
          </div>
        )}
      </Flutuante>
    </div>
  );
}
